import { dispatch } from "../store/store";
import { logErrorWithPayload } from "../services/logService";
import {
  getCompanyProductRulesByCompanyIdData,
  addCompanyProductRuleData,
  editCompanyProductRuleData,
  searchProductDrugData,
  removeCompanyProductRuleData,
  getProductRuleAuditData,
  getMonthlyConcessionData,
  getHistoricalConcessionData,
  addConcessionData,
  getTitanClaimsData,
  getPrescriptionImageData,
  getItemInfoData,
  saveClaimsData,
  getBlockedClaimsData,
  addEndorsementData,
  addUpdateExemptionData,
  getExemptionData,
  getDashboardStatsData,
  setIsTitanTabLoading,
  setCompanyProductRules,
  getGroupModeServicesData,
  getFp34SummaryData,
  getPharmacyPrivateServicesIncomeData,
  getPharmacyPrivateServicesIncomeByDateRangeData
} from "../features/titanTabSlice/titanTabSlice";

const reducerName = "titanTab";

// #region State Setters

export const setTitanTabLoading = (isLoading) => {
  dispatch(setIsTitanTabLoading(isLoading));
};

export const setCompanyProductRulesData = (data) => {
  dispatch(setCompanyProductRules(data));
};

// #endregion

// #region API getter/caller functions for components usage to dispatch REDUX extra reducers

export const getCompanyProductRulesByCompanyId = async (companyId) => {
  let result = [];
  try {
    result = await dispatch(
      getCompanyProductRulesByCompanyIdData(companyId)
    ).unwrap();
  } catch (error) {
    logErrorWithPayload(error, companyId);
    result = [];
  }
  return result;
};

export const addCompanyProductRule = async (payload) => {
  let result = null;
  try {
    result = await dispatch(addCompanyProductRuleData(payload)).unwrap();
  } catch (error) {
    logErrorWithPayload(error, payload);
    result = null;
  }
  return result;
};

export const editCompanyProductRule = async (payload) => {
  let result = null;
  try {
    result = await dispatch(editCompanyProductRuleData(payload)).unwrap();
  } catch (error) {
    logErrorWithPayload(error, payload);
    result = null;
  }
  return result;
};

export const searchProductDrug = async (keyword) => {
  let result = [];
  try {
    result = await dispatch(searchProductDrugData(keyword)).unwrap();
  } catch (error) {
    logErrorWithPayload(error, keyword);
    result = [];
  }
  return result;
};

export const removeCompanyProductRule = async (prescribedItemId, productRuleId) => {
  let result = null;
  try {
    result = await dispatch(
      removeCompanyProductRuleData({ prescribedItemId, productRuleId })
    ).unwrap();
  } catch (error) {
    logErrorWithPayload(error, { prescribedItemId, productRuleId });
    result = null;
  }
  return result;
};

export const getHistoricalConcession = async () => {
  let result = [];
  try {
    result = await dispatch(getHistoricalConcessionData()).unwrap();
  } catch (error) {
    logErrorWithPayload(error);
    result = [];
  }
  return result;
};

export const addConcession = async (payload) => {
  let result = null;
  try {
    result = await dispatch(addConcessionData(payload)).unwrap();
  } catch (error) {
    logErrorWithPayload(error, payload);
    result = null;
  }
  return result;
};

export const getMonthlyConcession = async (concessionMonth) => {
  let result = [];
  try {
    result = await dispatch(getMonthlyConcessionData(concessionMonth)).unwrap();
  } catch (error) {
    logErrorWithPayload(error, concessionMonth);
    result = [];
  }
  return result;
};

export const getProductRuleAudit = async (prescribedItemId, productRuleId) => {
  let result = null;
  try {
    result = await dispatch(
      getProductRuleAuditData({ prescribedItemId, productRuleId })
    ).unwrap();
  } catch (error) {
    logErrorWithPayload(error, { prescribedItemId, productRuleId });
    result = null;
  }
  return result;
};

export const getTitanClaims = async (pharmacyId) => {
  let result = [];
  try {
    result = await dispatch(getTitanClaimsData(pharmacyId)).unwrap();
  } catch (error) {
    logErrorWithPayload(error, pharmacyId);
    result = [];
  }
  return result;
};

export const getPrescriptionImage = async (pharmacyId, incomingId) => {
  let result = [];
  try {
    const respData = await dispatch(
      getPrescriptionImageData({ pharmacyId, incomingId })
    ).unwrap();
    result = respData?.data || [];
  } catch (error) {
    logErrorWithPayload(error, { pharmacyId, incomingId });
    result = [];
  }
  return result;
};

// Product/pack candidates for a set of prescribed items (get-item-info).
export const getItemInfo = async (pharmacyId, prescribedItems) => {
  try {
    const respData = await dispatch(
      getItemInfoData({ pharmacyId, prescribedItems })
    ).unwrap();
    if (Array.isArray(respData)) return respData;
    return respData?.response ?? respData?.data ?? [];
  } catch (error) {
    logErrorWithPayload(error, { pharmacyId, prescribedItems });
    return [];
  }
};

// FP34 total summary for a pharmacy/cycle (get-fp34-summary).
export const getFp34Summary = async (pharmacyId, cycleMonth) => {
  try {
    const respData = await dispatch(
      getFp34SummaryData({ pharmacyId, cycleMonth })
    ).unwrap();
    return respData?.response ?? respData?.data ?? respData ?? null;
  } catch (error) {
    logErrorWithPayload(error, { pharmacyId, cycleMonth });
    return null;
  }
};

// Private services income breakdown for a pharmacy/month (get-pharmacy-private-services-income).
export const getPharmacyPrivateServicesIncome = async (companyId, pharmacyId, month) => {
  try {
    const respData = await dispatch(
      getPharmacyPrivateServicesIncomeData({ companyId, pharmacyId, month })
    ).unwrap();
    return respData?.response ?? respData?.data ?? respData ?? null;
  } catch (error) {
    logErrorWithPayload(error, { companyId, pharmacyId, month });
    return null;
  }
};

export const getPharmacyPrivateServicesIncomeByDateRange = async (
  companyId,
  pharmacyId,
  startDate,
  endDate,
) => {
  try {
    const respData = await dispatch(
      getPharmacyPrivateServicesIncomeByDateRangeData({ companyId, pharmacyId, startDate, endDate })
    ).unwrap();
    return respData?.response ?? respData?.data ?? respData ?? null;
  } catch (error) {
    logErrorWithPayload(error, { companyId, pharmacyId, startDate, endDate });
    return null;
  }
};

export const saveClaims = async (pharmacyId, prescriptionIds) => {
  let result = null;
  try {
    const respData = await dispatch(
      saveClaimsData({ pharmacyId, prescriptionIds })
    ).unwrap();
    result = respData?.data || null;
  } catch (error) {
    logErrorWithPayload(error, { pharmacyId, prescriptionIds });
    result = null;
  }
  return result;
};

export const getBlockedClaims = async (pharmacyId) => {
  let result = [];
  try {
    result = await dispatch(getBlockedClaimsData(pharmacyId)).unwrap();
  } catch (error) {
    logErrorWithPayload(error, pharmacyId);
    result = [];
  }
  return result;
};

export const addEndorsement = async (
  pharmacyId,
  prescriptionItemId,
  additionalEndorses
) => {
  let result = null;
  try {
    const respData = await dispatch(
      addEndorsementData({ pharmacyId, prescriptionItemId, additionalEndorses })
    ).unwrap();
    result = respData?.data ?? null;
  } catch (error) {
    logErrorWithPayload(error, {
      pharmacyId,
      prescriptionItemId,
      additionalEndorses,
    });
    result = null;
  }
  return result;
};

export const addUpdateExemption = async (pharmacyId, exemption) => {
  let result = null;
  try {
    const respData = await dispatch(
      addUpdateExemptionData({ pharmacyId, exemption })
    ).unwrap();
    result = respData?.data ?? respData ?? null;
  } catch (error) {
    logErrorWithPayload(error, { pharmacyId, exemption });
    result = null;
  }
  return result;
};

export const getExemption = async (pharmacyId, prescriptionId) => {
  try {
    const respData = await dispatch(
      getExemptionData({ pharmacyId, prescriptionId })
    ).unwrap();
    return respData?.response ?? respData?.data ?? respData ?? null;
  } catch (error) {
    logErrorWithPayload(error, { pharmacyId, prescriptionId });
    return null;
  }
};

export const getGroupModeServices = async (companyID) => {
  let result = [];
  try {
    result = await dispatch(getGroupModeServicesData(companyID)).unwrap();
  } catch (error) {
    logErrorWithPayload(error, companyID);
    result = [];
  }
  return result;
};


export const getDashboardStats = async (companyId) => {
  let result = null;
  try {
    result = await dispatch(getDashboardStatsData(companyId)).unwrap();
  } catch (error) {
    logErrorWithPayload(error, companyId);
    result = null;
  }
  return result;
};

// #endregion
